// Raw design tokens for inline styles. Every token is a `var(--*)`
// reference, never a literal colour: the values are generated per theme
// from the ThemeDefinition objects in src/theme/themes/*.ts (buildCss.ts
// → themes.generated.css), so a `<html data-theme>` flip retunes every
// consumer with zero call-site changes.
//
// Three themes share this vocabulary:
//   glass    — astral / deep-space glassmorphism (default).
//   earth    — warm-dark editorial: espresso surfaces, terracotta /
//              ochre / olive / clay, flat-solid material.
//   midnight — neutral charcoal greys with a quiet white accent, flat.
//
// Prefer the Tailwind vocabulary in class names (text-fg, text-fg-muted,
// bg-panel, border-line, rounded-control …, see tailwind.config.ts); use
// these for inline styles. Type sizes are NOT tokens here: use the shared
// Tailwind ramp (text-xs … text-3xl) or var(--fs-12 … --fs-32).

/**
 * Named colour stops. Each is a `var(--c-*)` reference resolved by the
 * active theme. The accent family is the working interaction colour
 * (active states, buttons, pills); the text ladder is opacity-stepped
 * so hierarchy reads typographically rather than as dead grey.
 */
export const colors = {
  // Page + layer stack, darkest to elevated. `bg` is a fallback under
  // the ambient gradient; real rendering picks up `ambientLight`.
  bg: "var(--c-bg)",
  surface0: "var(--c-surface-0)",
  surface1: "var(--c-surface-1)",
  surface2: "var(--c-surface-2)",
  surface3: "var(--c-surface-3)",

  // Text ladder — opacity-stepped so the hierarchy reads as
  // typographic weight, not dimmed grey. Every step clears WCAG AA
  // (4.5:1) on bg, panel and raised in every theme.
  textPrimary: "var(--c-text-primary)",   // headings / card titles
  textMuted: "var(--c-text-muted)",        // body text
  textFaint: "var(--c-text-faint)",        // secondary, timestamps
  textGhost: "var(--c-text-ghost)",        // section labels, quiet ids

  // Accent family. `accent` is the FILL (primary buttons, toggles, the
  // selection bar) with `onAccent` text on it; `accentLight` is the
  // accent used as TEXT (links, active tab, icons); `accentDark` /
  // `accentDeeper` the shadow anchors.
  accent: "var(--c-accent)",
  accentLight: "var(--c-accent-light)",
  accentDark: "var(--c-accent-dark)",
  accentDeeper: "var(--c-accent-deeper)",
  onAccent: "var(--c-on-accent)",

  // Semantic state colours (AA as text). Feed the `statusBadge(accent)`
  // factory. `dangerFill` is the SOLID destructive / record fill with
  // `onDanger` text on it.
  success: "var(--c-success)",
  warning: "var(--c-warning)",
  danger: "var(--c-danger)",
  info: "var(--c-info)",
  dangerFill: "var(--c-danger-fill)",
  onDanger: "var(--c-on-danger)",

  // Focus ring (>= 3:1 against every surface).
  focus: "var(--c-focus)",

  // Border / edge overlays. Cards read as defined by their top-edge
  // highlight + shadow, not by a visible border line.
  borderHairline: "var(--c-border-hairline)",
  borderSubtle: "var(--c-border-subtle)",
  borderVisible: "var(--c-border-visible)",
  borderStrong: "var(--c-border-strong)",
  // Top-edge inset highlight — "light catching the front face".
  bevelTop: "var(--c-bevel-top)",
  bevelTopBright: "var(--c-bevel-top-bright)",
  bevelBottom: "var(--c-bevel-bottom)",
  bevelBottomDark: "var(--c-bevel-bottom-dark)",

  // Glass substrate — the base fill for each glass variant. Translucent
  // in glass, opaque warm in earth.
  glassPanel: "var(--c-glass-panel)",
  glassTile: "var(--c-glass-tile)",
  glassKpi: "var(--c-glass-kpi)",
  glassInset: "var(--c-glass-inset)",
  glassButton: "var(--c-glass-button)",
  glassButtonHover: "var(--c-glass-button-hover)",
  // Hover / raised / secondary-button fill, laid over a panel.
  raised: "var(--c-raised)",
  // OPAQUE surfaces: menus / dropdowns / dialogs, and bars (the companion
  // tab bar, sticky bars).
  overlay: "var(--c-overlay)",
  tabbarBg: "var(--c-tabbar-bg)",

  // Active selection — accent-tinted pill. Used by the sidebar's active
  // nav row, the primary button variant, and selected-state surfaces.
  glassActiveAccent: "var(--c-glass-active-accent)",
  glassActiveAccentBorder: "var(--c-glass-active-accent-border)",
  glassActiveAccentStrong: "var(--c-glass-active-accent-strong)",

  // Avatar fill — accent at low opacity so avatars blend into their
  // card rather than competing with it.
  avatarBg: "var(--c-avatar-bg)",
  avatarBorder: "var(--c-avatar-border)",
  avatarText: "var(--c-avatar-text)",

  // Sidebar chrome — slightly darker than the card substrate so the
  // left rail reads as recessed.
  sidebarBg: "var(--c-sidebar-bg)",
  sidebarEdge: "var(--c-sidebar-edge)",
} as const;

/**
 * Radius scale. Theme-driven via `var(--radius-*)` (each theme's
 * `radius`). Semantic names: `control` (= md; buttons, inputs, rows,
 * menu items), `card` (= xxl; cards, panels, dialogs), `pill` (badges,
 * chips, counts). Tailwind: rounded-control / rounded-card / rounded-pill.
 */
export const radius = {
  sm: "var(--radius-sm)",
  md: "var(--radius-md)",
  lg: "var(--radius-lg)",
  xl: "var(--radius-xl)",
  xxl: "var(--radius-xxl)",
  control: "var(--r-control)",
  card: "var(--r-card)",
  pill: "var(--r-pill)",
} as const;

/**
 * Agent identity hues (--id-1 … --id-8). One per agent, identical in
 * every theme. Pick with a stable hash of the agent id; this is the one
 * sanctioned source of per-agent colour.
 */
export const identityHues = [
  "var(--id-1)",
  "var(--id-2)",
  "var(--id-3)",
  "var(--id-4)",
  "var(--id-5)",
  "var(--id-6)",
  "var(--id-7)",
  "var(--id-8)",
] as const;

/**
 * Motion. Transitions snap at 100ms for state changes; 150ms for
 * colour tweens; 420ms for substantive morphs (sliding selectors,
 * panel transitions). Easing is Apple's stock smooth-out curve.
 */
export const motion = {
  fast: "100ms",
  normal: "150ms",
  slow: "420ms",
  ease: "cubic-bezier(0.32, 0.72, 0, 1)",
} as const;

/**
 * Common compound effects. Drop shadows resolve per theme via
 * `var(--shadow-*)` — restrained dark lifts in glass, soft warm lifts
 * in earth.
 */
export const effects = {
  panelLift: "var(--shadow-panel)",
  tileLift: "var(--shadow-tile)",
  subtleLift: "var(--shadow-subtle)",
  sidebarEdgeRight: "var(--shadow-sidebar-edge)",
  accentHalo: "var(--shadow-accent-halo)",
  accentHaloStrong: "var(--shadow-accent-halo-strong)",
} as const;

/**
 * Shell ambient light layer. Resolves to `var(--ambient)` — the full
 * whole-viewport background shorthand for the active theme. Glass = a
 * near-black multi-stop gradient with faint astral radials; earth = a
 * warm espresso gradient with faint terracotta pools. The CSS body
 * background in index.css mirrors this for early paint.
 */
export const ambientLight = "var(--ambient)";
